import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Modal,
  Input,
  InputTypeSelectAddress,
  generateRandomId,
  getCountryByLabel,
  LoadingSpinner,
} from '@rabbit/elements/shared-components';
import { AppContext } from '@rabbit/app-context';
import { useTranslation } from 'react-i18next';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { LIST_COUNTRIES } from '@rabbit/bizproc/react';
import { SelectAddressShape } from '@rabbit/elements/shared-types';
import { toast } from 'react-toastify';
import { Address, DTConsumer_Private } from '@rabbit/data/types';
import { DR_Consumer_Private, usePortalDocumentEditor } from '@rabbit/data/portal';
import { getPrimaryAddress } from '../../../utils/addressValidation';

const ADDRESS_FIELDS_MEANINGFUL = [
  'line1',
  'line2',
  'town',
  'state',
  'postcode',
] as const;

const ADDRESS_FIELDS_REQUIRED = ['line1', 'town', 'postcode', 'country'] as const;

function toDigits(value: unknown): string {
  return (value ?? '').toString().replace(/\D/g, '').trim();
}

function isPlausibleFullPhoneNumber(phoneDigits: string): boolean {
  return /^\d{8,15}$/.test(phoneDigits);
}

function isDialCodeOnlyPlaceholder(phoneDigits: string): boolean {
  // e.g. "61" / "44" etc (react-phone-input-2 can leave the dial code in the field)
  return phoneDigits.length > 0 && phoneDigits.length <= 3;
}

function hasAnyMeaningfulAddressInput(values: any): boolean {
  // Country is typically prefilled; don't treat it as user input.
  return ADDRESS_FIELDS_MEANINGFUL.some(
    (k) => (values?.[k] ?? '').toString().trim().length > 0
  );
}

function getMissingRequiredAddressFields(values: any): string[] {
  return ADDRESS_FIELDS_REQUIRED.filter(
    (k) => (values?.[k] ?? '').toString().trim().length === 0
  );
}

export interface ContactDetailsRequiredModalProps {
  isOpen: boolean;
  onClose: () => void;
  onProceedAnyway?: () => void;
  consumerPersonaId: string;
  redirectTo?: string;
  /** Optional: allow caller to specify what is missing so we can show the right CTA(s). */
  missingAddress?: boolean;
  missingPhoneNumber?: boolean;
}

export function ContactDetailsRequiredModal({
  isOpen,
  onClose,
  onProceedAnyway,
  consumerPersonaId,
  redirectTo,
  missingAddress = true,
  missingPhoneNumber = false,
}: ContactDetailsRequiredModalProps) {
  const { t } = useTranslation();
  const { tenantInfo, config } = useContext(AppContext);
  const navigate = useNavigate();
  const { body, update, commit, isReady } = usePortalDocumentEditor(
    DR_Consumer_Private,
    consumerPersonaId
  );
  const [isSaving, setIsSaving] = useState(false);
  const formikRef = useRef<any>(null);
  const [selectedAddressSuggestion, setSelectedAddressSuggestion] =
    useState<SelectAddressShape | null>(null);

  const handleProceedAnyway = () => {
    if (onProceedAnyway) {
      onProceedAnyway();
    }
    onClose();
  };

  if (!isOpen) return null;

  const isAddressRequiredForClaim = !!config?.CUSTOMER?.CLAIM_ADDRESS_MANDATORY;
  const isPhoneRequiredForClaim = !!config?.CUSTOMER?.CLAIM_PHONE_MANDATORY;

  const title = t('Contact details required');

  const existingDefaultAddress = useMemo(() => {
    return getPrimaryAddress(body);
  }, [body?.address]);

  const validationSchema = useMemo(() => {
    const base = Yup.object({
      phone: Yup.string().trim(),
      phone_e164: Yup.string().trim(),
      country: Yup.string().trim(),
      line1: Yup.string().trim(),
      line2: Yup.string().trim(),
      town: Yup.string().trim(),
      state: Yup.string().trim(),
      postcode: Yup.string().trim(),
    });

    return base
      .shape({
        phone: Yup.string()
          .trim()
          .test(
            'phone-e164-valid',
            t('Please enter a valid mobile number'),
            function () {
              const phoneDigits = toDigits(this.parent?.phone_e164);

              // When phone is optional:
              // - allow empty
              // - allow "dial code only" placeholders (typically 1-3 digits) without blocking submit
              // - otherwise require a plausible full number (8-15 digits)
              if (!isPhoneRequiredForClaim) {
                if (!phoneDigits) return true;
                if (isDialCodeOnlyPlaceholder(phoneDigits)) return true;
                return isPlausibleFullPhoneNumber(phoneDigits);
              }

              // When required, enforce a plausible full number (8-15 digits).
              return isPlausibleFullPhoneNumber(phoneDigits);
            }
          ),
        ...(isAddressRequiredForClaim && {
          // Field-level required validation ensures Formik blocks submit reliably
          line1: Yup.string().trim().required(t('Please enter an address')),
          town: Yup.string().trim().required(t('Please enter a town/city')),
          postcode: Yup.string().trim().required(t('Please enter a post code')),
          country: Yup.string().trim().required(t('Please enter a country')),
        }),
      })
      .test(
        'address-complete-when-needed',
        t('Please complete your address details'),
        function (values) {
          const v: any = values ?? {};
          const anyProvided = hasAnyMeaningfulAddressInput(v);

          // If address is not required and user didn't provide any address fields, it's valid.
          if (!isAddressRequiredForClaim && !anyProvided) return true;

          // If address is required OR user started filling it, ensure it's complete.
          const missing = getMissingRequiredAddressFields(v);
          if (missing.length === 0) return true;

          // Attach the error to the first missing field so Formik reliably blocks submit
          return this.createError({
            path: missing[0],
            message: t('Please complete your address details'),
          });
        }
      );
  }, [isAddressRequiredForClaim, isPhoneRequiredForClaim, t]);

  const formInitialValues = useMemo(() => {
    return {
      phone: body?.phone ?? '',
      phone_e164: body?.phone ?? '',
      country: existingDefaultAddress?.country ?? tenantInfo?.country ?? '',
      line1: existingDefaultAddress?.line1 ?? '',
      line2: existingDefaultAddress?.line2 ?? '',
      town: existingDefaultAddress?.town ?? '',
      state: existingDefaultAddress?.county ?? '',
      postcode: existingDefaultAddress?.postcode ?? '',
    };
  }, [body, existingDefaultAddress, tenantInfo?.country]);

  useEffect(() => {
    if (!selectedAddressSuggestion) return;
    const formik = formikRef.current;
    if (!formik || !selectedAddressSuggestion?.postal_code) return;

    // Set all fields in one shot so validation sees a consistent snapshot.
    (async () => {
      const nextValues = {
        ...formik.values,
        line1: selectedAddressSuggestion.line_1,
        line2: selectedAddressSuggestion.line_2,
        town: selectedAddressSuggestion.locality,
        state: selectedAddressSuggestion.province_name,
        postcode: selectedAddressSuggestion.postal_code,
        country:
          getCountryByLabel(selectedAddressSuggestion.country_name)?.value ?? '',
      };

      // Update without validating on every field update
      await formik.setValues(nextValues, false);

      // Mark the core fields touched so any remaining errors are immediately visible/accurate
      formik.setTouched(
        {
          ...formik.touched,
          line1: true,
          town: true,
          postcode: true,
          country: true,
        },
        false
      );

      // Now validate once and let Formik clear stale errors immediately
      await formik.validateForm();
    })().catch(() => {
      // ignore autofill validation errors
    });
  }, [selectedAddressSuggestion]);

  const upsertAsDefaultAddress = (
    consumer: DTConsumer_Private,
    address: Address
  ): Address[] => {
    const existing = Array.isArray(consumer.address) ? [...consumer.address] : [];
    const cleared = existing.map((a: any) => ({ ...a, isDefault: false }));
    const idx = cleared.findIndex((a: any) => a?.id === address.id);
    if (idx >= 0) {
      cleared[idx] = address;
      return cleared;
    }
    return [...cleared, address];
  };

  const handleSubmit = async (values: any) => {
    if (!body || !isReady) return;
    setIsSaving(true);
    try {
      const next: DTConsumer_Private = { ...body };

      const nextPhoneDigits = toDigits(values.phone_e164 ?? values.phone);
      // Never save empty/placeholder phone values.
      // - For required phone: validation should already block, but keep a defensive guard.
      // - For optional phone: only save if it's a plausible full number.
      if (nextPhoneDigits) {
        if (isPlausibleFullPhoneNumber(nextPhoneDigits)) {
          next.phone = nextPhoneDigits;
        } else if (isPhoneRequiredForClaim) {
          toast.error(t('Please enter a valid mobile number'));
          return;
        }
      }

      // Country is typically prefilled; don't treat it as "user provided address"
      const anyAddressProvided = hasAnyMeaningfulAddressInput(values);

      if (isAddressRequiredForClaim || anyAddressProvided) {
        // Defensive: never persist an incomplete required address even if validation doesn't fire
        const missing = getMissingRequiredAddressFields(values);
        if (missing.length > 0) {
          toast.error(t('Please complete your address details'));
          return;
        }

        const addressInfo: Address = {
          id: existingDefaultAddress?.id ?? generateRandomId(),
          label: existingDefaultAddress?.label ?? 'Home',
          line1: values.line1,
          line2: values.line2,
          company: '',
          town: values.town,
          postcode: values.postcode,
          county: values.state,
          country: values.country,
          isDefault: true,
        } as Address;
        next.address = upsertAsDefaultAddress(next, addressInfo);
      }

      // Prevent misleading "updated successfully" when nothing changed.
      const prevPhone = (body.phone ?? '').toString().trim();
      const nextPhone = (next.phone ?? '').toString().trim();
      const changedPhone = prevPhone !== nextPhone;
      const changedAddress =
        !!next.address &&
        JSON.stringify(body.address ?? []) !== JSON.stringify(next.address ?? []);

      if (!changedPhone && !changedAddress) {
        toast.info(t('No changes to save.'));
        onClose();
        if (redirectTo) navigate(redirectTo);
        return;
      }

      update(next);
      await commit();
      toast.success(t('Details updated successfully!'));
      onClose();
      if (redirectTo) {
        navigate(redirectTo);
      }
    } catch (err) {
      console.error(err);
      toast.error(t('Failed to update details. Please try again.'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      kind="generic"
      className="w-[92vw] max-w-[720px] md:w-[720px] lg:-mt-10"
      settings={{
        title,
        handleClose: onClose,
      }}
    >
      <div className="p-5 md:p-6">
        <div className="mb-4">
          <p className="text-sm text-gray-700 mb-2">
            {t(
              'To create a claim, please confirm your contact details. Fields marked * are required.'
            )}
          </p>
        </div>

        {!body ? (
          <LoadingSpinner size="sm" />
        ) : (
          <Formik
            initialValues={formInitialValues}
            enableReinitialize={true}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            innerRef={formikRef}
          >
            {({ errors, values }) => (
              <Form className="flex flex-col gap-4">
                {/* Scrollable body (keeps buttons stable on desktop) */}
                <div className="max-h-[55vh] md:max-h-[50vh] overflow-y-auto pr-1">
                  <div className="flex flex-col gap-3">
                    <div className="md:max-w-[520px]">
                      <Input
                        type="phone"
                        name="phone"
                        label={t('Mobile number')}
                        settings={{
                          id: 'phone',
                          placeholder: t('Mobile number'),
                          ...(isPhoneRequiredForClaim ? { hint: '*required' } : {}),
                        }}
                      />
                    </div>

                    <div className="md:grid md:grid-cols-2 md:gap-4">
                      <div className="md:col-span-2">
                        <Input
                          type="select"
                          label={t('Country')}
                          name="country"
                          settings={{
                            options: LIST_COUNTRIES,
                            ...(isAddressRequiredForClaim ? { hint: '*required' } : {}),
                          }}
                        />
                      </div>

                      <div className="md:col-span-2">
                        <div className="font-nunito pb-2 text-base text-gray-700">
                          {t('Find an address')}
                        </div>
                        <InputTypeSelectAddress
                          errors={null}
                          onChange={(address) => setSelectedAddressSuggestion(address)}
                          country={values.country}
                        />
                      </div>

                      <div className="md:col-span-2">
                        <Input
                          type="text"
                          label={t('Address line')}
                          name="line1"
                          settings={{
                            ...(isAddressRequiredForClaim ? { hint: '*required' } : {}),
                          }}
                        />
                      </div>

                      <div className="md:col-span-2">
                        <Input
                          type="text"
                          label={t('Address line 2')}
                          name="line2"
                          settings={{}}
                        />
                      </div>

                      <div>
                        <Input
                          type="text"
                          label={t('City/Suburb')}
                          name="town"
                          settings={{
                            ...(isAddressRequiredForClaim ? { hint: '*required' } : {}),
                          }}
                        />
                      </div>

                      <div>
                        <Input
                          type="text"
                          label={t('State/Province')}
                          name="state"
                          settings={{
                            ...(isAddressRequiredForClaim ? { hint: '*required' } : {}),
                          }}
                        />
                      </div>

                      <div className="md:col-span-1 md:max-w-[280px]">
                        <Input
                          type="text"
                          label={t('general.postCode')}
                          name="postcode"
                          settings={{
                            ...(isAddressRequiredForClaim ? { hint: '*required' } : {}),
                          }}
                        />
                      </div>
                      <div className="hidden md:block" />
                    </div>

                    {/* Helper text if the modal was opened due to missing required fields */}
                    {(missingAddress || missingPhoneNumber) && (
                      <p className="text-xs text-gray-500">
                        {t('Please fill in the required fields before continuing.')}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 pt-0.5">
                  <Button
                    kind="primary"
                    type="submit"
                    loading={isSaving}
                    disabled={isSaving || Object.keys(errors).length > 0}
                    className="flex-1"
                  >
                    {t('Save and continue')}
                  </Button>
                  <Button
                    kind="outline"
                    type="button"
                    onClick={onClose}
                    disabled={isSaving}
                    className="flex-1"
                  >
                    {t('Cancel')}
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        )}
      </div>
    </Modal>
  );
}

export default ContactDetailsRequiredModal;
